import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock, X } from "lucide-react";
import { Button } from "../../components/Button";
import { Label } from "../../components/Label";
import { CustomTextField } from "../../components/CustomTextField";
import { CustomTextArea } from "../../components/CustomTextArea";
import { CustomSelector } from "../../components/CustomSelector";
import { Checkbox } from "../../components/Checkbox";
import { UnifiedModal } from "../../components/UnifiedModal";
import { MediaDropZone } from "../../components/MediaDropZone";
import { AlertNote } from "../../components/AlertNote";
import {
  getTicketHoursStatus,
  isNoHoursError,
  TICKET_HOURS_STATUS_QUERY_KEY,
} from "../../services/ticketHoursService";
import { formatMinutes } from "../../utils/numberFormat";
import { BUY_HOURS_PATH } from "../Settings/settingsPaths";

const NO_CLIENT_OPTION = "__no_client__";

const NoHoursState = ({ canPurchase, onPurchase, onClose }) => (
  <Stack spacing={3}>
    <AlertNote variant="warning">
      <strong>Tu agencia no tiene horas disponibles.</strong> Cada incidencia consume horas de soporte técnico, así
      que necesitas saldo para abrir una nueva.
      {canPurchase ? (
        <>
          {" "}Compra horas y podrás crearla en cuanto se confirme el pago. Puedes comprar horas en cualquier momento
          desde <strong>Ajustes › Horas de tickets</strong>.
        </>
      ) : (
        " Pide a un administrador de tu agencia que compre horas."
      )}
      {" "}Para dudas sobre la plataforma usa Soporte, que no consume horas.
    </AlertNote>
    <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
      <Button variant="outline" onClick={onClose}>
        {canPurchase ? "Cancelar" : "Cerrar"}
      </Button>
      {canPurchase && <Button onClick={onPurchase}>Comprar horas</Button>}
    </Stack>
  </Stack>
);

NoHoursState.propTypes = {
  canPurchase: PropTypes.bool,
  onPurchase: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

const ImageThumbnails = ({ files, onRemove }) => {
  const previews = useMemo(() => files.map((file) => URL.createObjectURL(file)), [files]);
  useEffect(() => () => previews.forEach((url) => URL.revokeObjectURL(url)), [previews]);

  if (!files.length) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
      {files.map((file, index) => (
        <Box
          key={`${file.name}-${index}`}
          sx={{ position: "relative", width: 72, height: 72, borderRadius: "8px", overflow: "hidden", border: "1px solid", borderColor: "divider" }}
        >
          <Box component="img" src={previews[index]} alt={file.name} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
          <IconButton
            size="small"
            aria-label={`Quitar ${file.name}`}
            onClick={() => onRemove(index)}
            sx={{ position: "absolute", top: 2, right: 2, p: "2px", backgroundColor: "rgba(0,0,0,0.55)", color: "#fff", "&:hover": { backgroundColor: "rgba(0,0,0,0.75)" } }}
          >
            <X size={12} />
          </IconButton>
        </Box>
      ))}
    </Box>
  );
};

export const CreateIncidentModal = ({
  isOpen,
  onClose,
  currentUser,
  onSubmit,
  clients = [],
  // "support": consulta sin cliente, urgencia ni horas
  variant = "incident",
}) => {
  const theme = useMuiTheme();
  const isSupport = variant === "support";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [noHoursRejected, setNoHoursRejected] = useState(false);

  // Toda agencia necesita saldo para abrir incidencias; el superadmin no
  const needsPurchasedHours = !isSupport && currentUser?.role !== "superadmin";
  const { data: hoursStatus, isLoading: isLoadingHours } = useQuery({
    queryKey: TICKET_HOURS_STATUS_QUERY_KEY,
    queryFn: getTicketHoursStatus,
    enabled: isOpen && needsPurchasedHours,
    staleTime: 30_000,
  });
  // Si el endpoint falla no se bloquea: el POST lo vuelve a comprobar
  const isBlocked = needsPurchasedHours && (noHoursRejected || hoursStatus?.can_create === false);
  const [clientSelectionError, setClientSelectionError] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    client_id: "",
    is_urgent: false,
    ticket_image_file: null,
    ticket_image_files: [],
  });

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      client_id: "",
      is_urgent: false,
      ticket_image_file: null,
      ticket_image_files: [],
    });
    setClientSelectionError(false);
    setNoHoursRejected(false);
  };

  const handleModalClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.description.trim()) {
      return;
    }

    if (!isSupport && !formData.client_id) {
      setClientSelectionError(true);
      return;
    }

    try {
      setIsSubmitting(true);

      // Llamar a onSubmit que se encarga de crear el ticket
      if (onSubmit) {
        const payload = {
          ...formData,
          client_id:
            !formData.client_id || formData.client_id === NO_CLIENT_OPTION
              ? null
              : formData.client_id,
        };
        await onSubmit(payload);
      }
      handleModalClose();
    } catch (error) {
      if (isNoHoursError(error)) {
        setNoHoursRejected(true);
        queryClient.invalidateQueries({ queryKey: TICKET_HOURS_STATUS_QUERY_KEY });
      } else {
        // onSubmit ya muestra el error al usuario
        handleModalClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToBuyHours = () => {
    handleModalClose();
    navigate(BUY_HOURS_PATH);
  };

  return (
    <UnifiedModal
      open={isOpen}
      onClose={handleModalClose}
      title={isSupport ? "Nueva consulta sobre la plataforma" : "Crear Nuevo Ticket"}
    >
      {isLoadingHours ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={24} />
        </Box>
      ) : isBlocked ? (
        <NoHoursState
          canPurchase={hoursStatus?.can_purchase}
          onPurchase={goToBuyHours}
          onClose={handleModalClose}
        />
      ) : (
      <Stack spacing={3}>
        {needsPurchasedHours && hoursStatus && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "text.secondary" }}>
            <Clock size={14} />
            <Typography variant="body2" color="text.secondary">
              Te quedan {formatMinutes(hoursStatus.remaining_minutes)} de soporte técnico.
            </Typography>
          </Box>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Título */}
            <Box>
              <Label sx={{ fontSize: "1rem" }}>
                {isSupport ? "Asunto" : "Título del ticket"}{" "}
                <Box
                  component="span"
                  sx={{
                    color: theme.palette.error.main,
                  }}
                >
                  *
                </Box>
              </Label>
              <CustomTextField
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                placeholder={isSupport ? "Ej. ¿Cómo creo un nuevo proyecto?" : "Ej. Problema con el cliente X"}
                required
                sx={{ marginTop: "8px" }}
              />
            </Box>

            {/* Descripción */}
            <Box>
              <Label sx={{ fontSize: "1rem" }}>
                Descripción{" "}
                <Box
                  component="span"
                  sx={{
                    color: theme.palette.error.main,
                  }}
                >
                  *
                </Box>
              </Label>
              <CustomTextArea
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder={isSupport ? "Cuéntanos tu duda sobre cómo usar la plataforma..." : "Describe los detalles del ticket..."}
                required
                sx={{ marginTop: "8px", minHeight: "100px" }}
              />
            </Box>

            {!isSupport && (
            <Box>
              <Label sx={{ fontSize: "1rem" }}>
                Cliente asociado{" "}
                <Box
                  component="span"
                  sx={{
                    color: theme.palette.error.main,
                  }}
                >
                  *
                </Box>
              </Label>
              <Box sx={{ marginTop: "8px" }}>
                <CustomSelector
                  value={formData.client_id}
                  onChange={(value) =>
                    {
                      setClientSelectionError(false);
                      setFormData((prev) => ({
                        ...prev,
                        client_id: value,
                      }));
                    }
                  }
                  options={[
                    { value: NO_CLIENT_OPTION, label: "Sin cliente" },
                    ...clients.map((client) => ({
                      value: client.id.toString(),
                      label: client.name || client.company_name || `Cliente ${client.id}`,
                    })),
                  ]}
                  placeholder="Selecciona un cliente"
                  searchable
                />
              </Box>
              {clientSelectionError && (
                <Typography
                  variant="body2"
                  sx={{
                    marginTop: "8px",
                    fontSize: "0.8125rem",
                    color: theme.palette.error.main,
                    lineHeight: 1.4,
                  }}
                >
                  Selecciona una opción.
                </Typography>
              )}
              <Box
                sx={{
                  marginTop: "10px",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: `1px solid ${theme.palette.warning.light || theme.palette.divider}`,
                  backgroundColor: theme.palette.warning.lighter || `${theme.palette.warning.main}12`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.8125rem",
                    color: theme.palette.warning.dark || theme.palette.text.secondary,
                    lineHeight: 1.45,
                  }}
                >
                  Seleccionar un cliente es obligatorio, a excepción de otras gestiones.
                </Typography>
              </Box>
            </Box>
            )}

            {isSupport ? (
              <Box>
                <Label sx={{ fontSize: "1rem" }}>Imágenes (opcional)</Label>
                <Box sx={{ marginTop: "8px" }}>
                  <MediaDropZone
                    multiple
                    title="Capturas de pantalla"
                    onFiles={(files) =>
                      setFormData((prev) => ({
                        ...prev,
                        ticket_image_files: [
                          ...prev.ticket_image_files,
                          ...Array.from(files).filter((file) => file.type.startsWith("image/")),
                        ],
                      }))
                    }
                  />
                  <ImageThumbnails
                    files={formData.ticket_image_files}
                    onRemove={(index) =>
                      setFormData((prev) => ({
                        ...prev,
                        ticket_image_files: prev.ticket_image_files.filter((_, i) => i !== index),
                      }))
                    }
                  />
                </Box>
              </Box>
            ) : (
              <Box>
                <Label sx={{ fontSize: "1rem" }}>Imagen (opcional)</Label>
                <Box sx={{ marginTop: "8px" }}>
                  <MediaDropZone
                    title="Captura o imagen de la incidencia"
                    onFiles={(files) => {
                      const file = Array.from(files).find((f) => f.type.startsWith("image/"));
                      if (file) setFormData((prev) => ({ ...prev, ticket_image_file: file }));
                    }}
                  />
                  <ImageThumbnails
                    files={formData.ticket_image_file ? [formData.ticket_image_file] : []}
                    onRemove={() => setFormData((prev) => ({ ...prev, ticket_image_file: null }))}
                  />
                </Box>
              </Box>
            )}

            {!isSupport && (
            <Box>
              <Label htmlFor="is-urgent-ticket" sx={{ fontSize: "1rem" }}>
                Ticket urgente?
              </Label>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ marginTop: "8px" }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Checkbox
                    id="is-urgent-ticket-no"
                    checked={!formData.is_urgent}
                    onCheckedChange={() =>
                      setFormData((prev) => ({ ...prev, is_urgent: false }))
                    }
                  />
                  <Label htmlFor="is-urgent-ticket-no" sx={{ marginBottom: 0, fontWeight: 400 }}>
                    No
                  </Label>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Checkbox
                    id="is-urgent-ticket-yes"
                    checked={formData.is_urgent}
                    onCheckedChange={() =>
                      setFormData((prev) => ({ ...prev, is_urgent: true }))
                    }
                  />
                  <Label htmlFor="is-urgent-ticket-yes" sx={{ marginBottom: 0, fontWeight: 400 }}>
                    Sí
                  </Label>
                </Stack>
              </Stack>
            </Box>
            )}


            {/* Footer buttons */}
            <Stack
              direction="row"
              justifyContent="flex-end"
              spacing={1.5}
              sx={{ paddingTop: "24px" }}
            >
              <Button
                variant="outline"
                onClick={handleModalClose}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={
                  isSubmitting ||
                  !formData.title.trim() ||
                  !formData.description.trim()
                }
              >
                {isSubmitting ? (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box
                      sx={{
                        width: "16px",
                        height: "16px",
                        marginRight: "8px",
                        border: "1px solid currentColor",
                        borderTop: "1px solid transparent",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                        "@keyframes spin": {
                          "0%": { transform: "rotate(0deg)" },
                          "100%": { transform: "rotate(360deg)" },
                        },
                      }}
                    />
                    Creando...
                  </Box>
                ) : (
                  isSupport ? "Enviar consulta" : "Crear Ticket"
                )}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Stack>
      )}
    </UnifiedModal>
  );
};
